import React, { PropsWithChildren, RefObject } from 'react'

import { useNavigate } from '@lib/react-router-dom'

import { ProductRiskWarning } from '@pages/ProductRegistration/ProductRiskWarning'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useLossPercentagesReadContext } from '@context/LossPercentagesContext'
import { useModal } from '@context/ModalsContext'
import { useProductReadContext } from '@context/ProductContext'

import { Heading6 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { CloseIcon } from '@ui/icons/Close.icon'

import { isTickmillPartner } from '@utils/companyName.utils'
import { navigateBasedOnProductType } from '@utils/path'

import { Box } from '@shared/components/Box'
import { ActionModal } from '@shared/modals/Action.modal'
import { RiskWarningModal } from '@shared/modals/RiskWarning.modal'
import { FullScreenLayout } from '@shared/templates/FullScreenLayout'

interface DepositTemplateProps extends PropsWithChildren {
  maxWidth?: number
  isLoading?: boolean
  contentRef?: RefObject<HTMLDivElement | null>
  isFirstSection?: boolean
  handleChangeStepPrev?: () => void
  onClose?: () => void
  onExit?: () => void
  title?: string
  noHeader?: boolean
  noExit?: boolean
}

export const TestTemplate: React.FC<DepositTemplateProps> = (props) => {
  const {
    isLoading,
    isFirstSection,
    handleChangeStepPrev,
    children,
    maxWidth,
    contentRef,
    title,
    onClose,
    onExit,
    noHeader,
    noExit,
  } = props
  const { account } = useAccountReadContext()
  const { product, isDefaultCFDProductType } = useProductReadContext()
  const { percentages } = useLossPercentagesReadContext()
  const isCFDProduct = isDefaultCFDProductType()
  const navigate = useNavigate()
  const isPartner = isTickmillPartner(account)

  const exitConfirmationModal = useModal(ActionModal, {
    onConfirm: () => {
      if (onExit) {
        onExit()
        return
      }

      navigateBasedOnProductType(isCFDProduct, navigate, isPartner)
    },
  })

  const riskWarningModal = useModal(RiskWarningModal, {
    selectedProductIds: [product],
    risk: percentages?.percentage,
  })
  const handleHeaderLeftClick = () => {
    if (isFirstSection === false && handleChangeStepPrev) return handleChangeStepPrev()
    if (onClose) return onClose()
    exitConfirmationModal.open()
  }

  return (
    <FullScreenLayout
      maxContentWidth={maxWidth}
      contentRef={contentRef}
      isLoading={isLoading}
      headerLeftSlot={
        !noHeader &&
        !noExit && (
          <IconButton.Ghost
            icon={isFirstSection === false ? ArrowIcon.Left : CloseIcon}
            onClick={handleHeaderLeftClick}
          />
        )
      }
      headerRightSlot={!noHeader && <ProductRiskWarning onClick={() => riskWarningModal.open()} />}
      header={
        title && (
          <Box justify='center' fullWidth flex>
            <Heading6 medium>{title}</Heading6>
          </Box>
        )
      }
    >
      <Box flex direction='column' gap='x32' fullWidth align='center' minHeight='100%'>
        {children}
      </Box>
    </FullScreenLayout>
  )
}
